import { canv } from "@/canv/global";
import {
    getBoolProperty,
    getColorProperty,
    getNumberProperty,
    getStringProperty,
    isBrowser,
    lightOrDark,
} from "@/canv/utils";
import { ElementBase } from "@/elementBase";
import { frameOverlappedManager } from "@/elements/frame/FrameOverlappedManager";
import { regenerateTextPartUID } from "@/elements/text/textPartFactory";
import { TextWidgetV2 } from "@/elements/text/textV2";
import type { Group } from "@/group";
import type { Area } from "@/group/types";
import { widgetOption } from "@/widgetMenu/widgetMenu";
import { EmbedWidget } from "../embed";
import { PresentationWidget } from "../Presentation/Presentation";
import type {
    FrameStateColor,
    FrameStateFixType,
    FrameStateFull,
    FrameStateHide,
    FrameStateMap,
    FrameStateMove,
    FrameStateResize,
} from "./types";
import { AspectRatioEnum } from "./types";

export const MIN_SIZE = 50;

export class frameWidget extends ElementBase {
    constructor(startX: number, startY: number, width?: number, height?: number) {
        super();
        this.startX = startX;
        this.startY = startY;
        this.width = width ?? 250;
        this.height = height ?? 250;

        this.textName = new TextWidgetV2();
        this.textName.setText("Frame");

        this.textName.menuTextSize(21);

        this.textName.menuTextColor("white");
        this.textName.setKeepMinWidth(false);
        this.textName.onlyLineMode = true;

        this.textName.setEditingAutosaveHandler(() => {
            canv.eventManager.openRecord();
            this.saveState("nameFocus");
            canv.eventManager.closeRecord();
            this.fixState("nameFocus");
        });

        this.rotatable = false;

        this.updateDims();
    }

    updateDims() {
        this.updateBase(this.startX, this.startY, this.width, this.height);
    }

    regenerateUIDS(): void {
        regenerateTextPartUID(this.textName, this.UID);
    }

    // TryAction -----------------------------------------------

    TryAction(type: string) {
        this.textName.TryAction(type);
        this.updateDims();

        if (type === "keyboardDown") {
            this.tryShortcut();
        }

        if (type === "mousedown" && !this.isOver()) {
            const tool = canv.toolBox.TryGetToolByName("cursorTool");

            if (tool) {
                tool.turnMode("");
                tool.TryAction("mousedown");
            }
        }
    }

    isInteraction(context: string, force = false) {
        if (context === "focus" && this.shouldFocusText()) {
            this.isNameEditing = true;
            this.fixState("nameFocus");
            this.getTitleProps();
            this.textName.focusText(true);
            canv.textEditingController.syncAfterHostResize();
            return {
                type: "interaction",
            };
        }

        return { type: "none" };
    }

    stopInteraction() {
        const shouldSave = !this.textName.consumeHostInteractionSaveSkip();

        if (shouldSave) {
            canv.eventManager.openRecord();
            this.saveState("nameFocus");
            canv.eventManager.closeRecord();
        }

        if (this.isNameEditing) {
            this.isNameEditing = false;
            canv.textEditingController.cancel();
        }
    }

    tryShortcut() {
        if (canv.keyboard.isEscCommand()) {
            // stopInteraction вызывается внутри cursorTool при изменении mode
            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
        }
    }

    // --------------------------------------------------------------------

    // Utils

    getTitleProps() {
        // Считаем titleWidth, titleHeight. Меняем честный this.textName только если Focused, так
        // как менять его на каждый draw сильно есть производительность
        // Всегда просто рисуем текст через fillText, а когда Focused this.textName.draw()
        // getTitleProps всегда отдает то, что нарисовано

        const fontSize = canv.actionManager.getZoomConstant(16);
        const margin = canv.actionManager.getZoomConstant(7);
        const padding = canv.actionManager.getZoomConstant(9);
        const titleX = this.startX;
        let titleY = 0;
        let titleWidth = 0;
        let titleHeight = 0;
        let text = "";
        let isCutted = false;

        if (!this.isNameEditing) {
            const fullText = this.textName.getText();
            const firstLine = fullText.split("\n")[0] ?? "";
            text = firstLine;

            const fontFamily = this.textName.getData().fontFamily;
            canv.ctx.font = `${fontSize}px ${fontFamily}`;
            const metrics = canv.ctx.measureText(text);
            titleHeight = metrics.actualBoundingBoxAscent; //+ metrics.actualBoundingBoxDescent;
            titleWidth = metrics.width;

            titleY = this.startY - margin - titleHeight - 2 * padding;

            isCutted = 2 * margin + 2 * padding + titleWidth >= this.width;

            if (isCutted) {
                const dotsMetrics = canv.ctx.measureText("...");
                const itemMetrics = canv.ctx.measureText("ш");

                // сбрасываем
                text = "";
                titleWidth = 0;

                const maxWidth = this.width - 2 * margin - 2 * padding;
                for (let i = 0; i < firstLine.length; i++) {
                    if (titleWidth + dotsMetrics.width + itemMetrics.width >= maxWidth) {
                        break;
                    }

                    text += firstLine[i];
                    titleWidth += itemMetrics.width;
                }

                if (text) {
                    text += "...";
                    titleWidth += dotsMetrics.width;
                }
            }
        } else {
            text = this.textName.getText();
            const textName = this.textName;
            if (textName.fontSize !== fontSize) {
                textName.menuTextSize(fontSize);
            }

            titleY = this.startY - margin - textName.height - 2 * padding;

            textName.startX = titleX + padding - textName.horizontalPadding;
            textName.startY = titleY + padding + textName.lineHeight;

            textName.updateBase(
                textName.startX,
                textName.startY - textName.lineHeight,
                textName.width,
                textName.height,
            );
            canv.textEditingController.updateOverlayTransform();

            titleWidth = textName.width;
            titleHeight = textName.height;
        }

        return {
            padding: padding,
            margin: margin,
            fontSize: fontSize,
            titleWidth: titleWidth,
            titleHeight: titleHeight,
            startX: titleX,
            startY: titleY,
            text: text,
            hidden: isCutted && text === "",
        };
    }

    // Draw

    colorShade(colorString: string, amt: number) {
        let col = colorString.replace(/^#/, "");
        if (col.length === 3) col = col[0] + col[0] + col[1] + col[1] + col[2] + col[2];

        let [r, g, b] = col.match(/.{2}/g) as [string, string, string];

        r = Math.max(Math.min(255, Number.parseInt(r, 16) + amt), 0).toString(16);
        g = Math.max(Math.min(255, Number.parseInt(g, 16) + amt), 0).toString(16);
        b = Math.max(Math.min(255, Number.parseInt(b, 16) + amt), 0).toString(16);

        const rr = (r.length < 2 ? "0" : "") + r;
        const gg = (g.length < 2 ? "0" : "") + g;
        const bb = (b.length < 2 ? "0" : "") + b;

        return `#${rr}${gg}${bb}`;
    }

    draw() {
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(1);
        canv.ctx.beginPath();

        // Сам фрейм
        let backColor = "white";

        if (this.color === "transparent") {
            backColor = "transparent";
        } else if (this.colorMap[this.color]) {
            backColor = this.colorMap[this.color];
        } else {
            backColor = this.colorShade(this.color, 80);
        }

        if (backColor !== "transparent") {
            canv.ctx.fillStyle = backColor;
            this.drawRoundedRect(
                this.startX,
                this.startY,
                this.width,
                this.height,
                canv.actionManager.getZoomConstant(4),
            );
            canv.ctx.fill();
        }
    }

    drawBorder() {
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(1);
        canv.ctx.beginPath();

        let borderColor = this.color;
        if (this.color === "transparent") {
            borderColor = "#dadada";
        }

        canv.ctx.strokeStyle = borderColor;
        this.drawRoundedRect(
            this.startX,
            this.startY,
            this.width,
            this.height,
            canv.actionManager.getZoomConstant(4),
        );
        canv.ctx.stroke();
    }

    drawTitle() {
        // title
        const titleProps = this.getTitleProps();

        const titleBackColor = this.color;
        let textColor = "white";

        if (lightOrDark(this.color) === "light") {
            textColor = "black";
        } else {
            textColor = "white";
        }

        if (!titleProps.hidden) {
            if (!this.isNameEditing) {
                // Рисуем placeholder, иначе тормозит
                const fontFamily = this.textName.getData().fontFamily;
                canv.ctx.font = `${titleProps.fontSize.toString()}px ${fontFamily}`;
                if (titleBackColor !== "transparent") {
                    canv.ctx.fillStyle = titleBackColor;
                    this.drawRoundedRect(
                        titleProps.startX,
                        titleProps.startY,
                        2 * titleProps.padding + titleProps.titleWidth,
                        2 * titleProps.padding + titleProps.titleHeight,
                        canv.actionManager.getZoomConstant(4),
                    );
                    canv.ctx.fill();
                }

                canv.ctx.fillStyle = textColor;
                canv.ctx.fillText(
                    titleProps.text,
                    titleProps.startX + titleProps.padding,
                    titleProps.startY + titleProps.padding + titleProps.titleHeight,
                );
            } else {
                if (titleBackColor !== "transparent") {
                    canv.ctx.fillStyle = titleBackColor;
                    this.drawRoundedRect(
                        titleProps.startX,
                        titleProps.startY,
                        2 * titleProps.padding + titleProps.titleWidth,
                        2 * titleProps.padding + titleProps.titleHeight,
                        canv.actionManager.getZoomConstant(4),
                    );
                    canv.ctx.fill();
                }

                if (lightOrDark(this.color) === "light") {
                    this.textName.menuTextColor("black", true);
                } else {
                    this.textName.menuTextColor("white", true);
                }

                this.textName.draw();
            }
        }
    }

    drawOver() {
        this.drawBorder();
        this.drawTitle();

        if (!this.isHidden) {
            return;
        }

        canv.ctx.fillStyle = "#F5F5F5";
        this.drawRoundedRect(
            this.startX,
            this.startY,
            this.width,
            this.height,
            canv.actionManager.getZoomConstant(4),
        );
        canv.ctx.fill();

        const lineWidth = canv.actionManager.getConstant(15);
        const linesAmount = (this.width - lineWidth) / 2 / lineWidth;
        let posX = this.startX + lineWidth;

        for (let i = 0; i < linesAmount; i++) {
            let drawWidth = lineWidth;
            if (posX + lineWidth > this.startX + this.width) {
                drawWidth = this.startX + this.width - posX;
            }
            canv.ctx.fillStyle = "#E6E6E6";
            this.drawRoundedRect(
                posX,
                this.startY,
                drawWidth,
                this.height,
                canv.actionManager.getZoomConstant(4),
            );
            canv.ctx.fill();

            posX += 2 * lineWidth;
        }
    }

    // Helpers

    isOverVerticalLine(lineX: number, startY: number, endY: number) {
        const topY = startY < endY ? startY : endY;
        const bottomY = startY > endY ? startY : endY;
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const gap = canv.actionManager.getZoomConstant(5);

        return x > lineX - 2 - gap && x < lineX + 2 + gap && y > topY && y < bottomY;
    }

    isOverHorizontalLine(lineY: number, startX: number, endX: number) {
        const leftX = startX < endX ? startX : endX;
        const rightX = startX > endX ? startX : endX;
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const gap = canv.actionManager.getZoomConstant(5);

        return x > leftX && x < rightX && y > lineY - 2 - gap && y < lineY + 2 + gap;
    }

    drawRoundedRect(x: number, y: number, w: number, h: number, r: number) {
        if (w < 0 || h < 0 || r < 0) {
            return;
        }
        let radius = r;
        if (w < 2 * r) radius = w / 2;
        if (h < 2 * r) radius = h / 2;
        canv.ctx.beginPath();
        canv.ctx.moveTo(x + radius, y);
        canv.ctx.arcTo(x + w, y, x + w, y + h, radius);
        canv.ctx.arcTo(x + w, y + h, x, y + h, radius);
        canv.ctx.arcTo(x, y + h, x, y, radius);
        canv.ctx.arcTo(x, y, x + w, y, radius);
        canv.ctx.closePath();
    }

    isInArea(area: Area) {
        // Проверяем, что все углы элемента внутри области
        return (
            area.x <= this.baseStartX &&
            area.y <= this.baseStartY &&
            area.x + area.width >= this.baseStartX + this.baseWidth &&
            area.y + area.height >= this.baseStartY + this.baseHeight
        );
    }

    isOver(isCandidate?: boolean) {
        return !!this.getOverArea(isCandidate);
    }

    getOverArea(isCandidate?: boolean) {
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const titleProps = this.getTitleProps();
        if (!titleProps.hidden) {
            if (
                x > titleProps.startX &&
                x < titleProps.startX + titleProps.titleWidth + 2 * titleProps.padding &&
                y > titleProps.startY &&
                y < titleProps.startY + titleProps.titleHeight + 2 * titleProps.padding
            ) {
                return "title";
            }
        }

        // Если фрейм находится под виджетом, то его можно выбрать только кликнув по тайтлу
        if (isCandidate) return "";

        const leftBorder = this.isOverVerticalLine(
            this.startX,
            this.startY,
            this.startY + this.height,
        );
        const rightBorder = this.isOverVerticalLine(
            this.startX + this.width,
            this.startY,
            this.startY + this.height,
        );

        const topBorder = this.isOverHorizontalLine(
            this.startY,
            this.startX,
            this.startX + this.width,
        );
        const bottomBorder = this.isOverHorizontalLine(
            this.startY + this.height,
            this.startX,
            this.startX + this.width,
        );

        if (leftBorder || rightBorder || topBorder || bottomBorder) {
            return "border";
        }

        return x > this.baseStartX &&
            x < this.baseStartX + this.baseWidth &&
            y > this.baseStartY &&
            y < this.baseStartY + this.baseHeight
            ? "body"
            : "";
    }

    shouldFocusText() {
        const pt = canv.actionManager.getActionPoint();
        const x = pt.x;
        const y = pt.y;

        const titleProps = this.getTitleProps();

        if (titleProps.hidden) {
            return false;
        }

        if (
            x > titleProps.startX &&
            x < titleProps.startX + titleProps.titleWidth + 2 * titleProps.padding
        ) {
            if (
                y > titleProps.startY &&
                y < titleProps.startY + titleProps.titleHeight + 2 * titleProps.padding
            ) {
                return true;
            }
        }

        return false;
    }

    searchText(text: string) {
        const frameTextName = this.textName.getText().toLowerCase();

        if (frameTextName.includes(text)) {
            return [
                {
                    status: true,
                    textWidget: this.textName,
                    mode: "widget",
                },
            ];
        }

        return [];
    }

    getArea() {
        return {
            x: this.startX,
            y: this.startY,
            width: this.width,
            height: this.height,
        };
    }

    hasAsOverlapped(group: Group) {
        const area = this.getArea();
        const isInArea = group.isAnyItemInArea(area);

        return isInArea && group.indexByParent > this.parent.indexByParent;
    }

    // Выдает элементы которые размещаются на фрейме
    // Но при сгруппированых и залоченых элементах выдает либо элементы,
    // либо группу элементов либо ничего
    getOverlappedGroups(): Group[] {
        const groups: Group[] = [];

        const area = this.getArea();
        const selectedUIDs = new Set(
            canv.canvasController.selected
                .filter((s) => (this.presentationUID ? !this.hasAsOverlapped(s) : true))
                .map((s) => s.UID),
        );

        for (const item of canv.canvasController.items) {
            // Пропускаем элемент, если он содержит подгруппу
            if (item.hasSubGroup(this.parent)) continue;

            // Если элемент не находится в заданной области, пропускаем его
            if (!item.isAnyItemInArea(area)) continue;

            if (this.parent && this.parent.indexByParent < item.indexByParent) {
                if (item.parent) {
                    // Если родитель элемента уже выбран, пропускаем его
                    if (
                        selectedUIDs.has(item.parentUID) ||
                        groups.some((g) => g.UID === item.parentUID)
                    ) {
                        continue;
                    }

                    // Если родитель элемента находится в той же группе, что и фрейм, добавляем только сам элемент
                    // без родителя, иначе нарвемся на Maximum call stack size exceeded
                    if (item.parentUID === this.parent?.parentUID) {
                        groups.push(item);

                        continue;
                    }

                    // Если элемент уже выбран, пропускаем его
                    if (selectedUIDs.has(item.UID)) continue;

                    groups.push(item.parent);
                } else {
                    // Если элемент уже выбран, пропускаем его
                    if (selectedUIDs.has(item.UID) || selectedUIDs.has(item.parentUID)) continue;

                    groups.push(item);
                }
            }
        }

        return groups;
    }

    translatingStart() {
        const groups = this.getOverlappedGroups();

        for (const group of groups) {
            group.fixWidgets("move");
            frameOverlappedManager.addGroup(this.UID, group);
        }
    }

    translatingStop() {
        // Сохраняем состояние всех групп, захваченных этим фреймом
        const groups = frameOverlappedManager.getGroups(this.UID);
        for (const group of groups) {
            group.saveWidgets("move");
        }
    }

    rotatingStart() {
        this.rotablesWidgetsUids = [];
        const rotatingGroups: Group[] = [];
        const groups = this.getOverlappedGroups();

        for (const group of groups) {
            group.fixWidgets("move");
            rotatingGroups.push(group);

            const child = group.onlyChild();
            if (child?.rotatable) {
                child.rotatable = false;
                this.rotablesWidgetsUids.push(group.UID);
            }
        }

        this.rotatingGroups = rotatingGroups;
    }

    rotatingStop() {
        if (!this.rotatingGroups) return;

        for (const item of this.rotatingGroups) {
            item.saveWidgets("move");

            // Восстанавливаем возможность переворота виджета в фрейме
            if (this.rotablesWidgetsUids.includes(item.UID)) {
                const child = item.onlyChild();
                if (child) {
                    child.rotatable = true;
                }
            }
        }

        this.rotatingGroups = null;
        this.rotablesWidgetsUids = [];
    }

    updateAfterMobileEdit() {
        canv.eventManager.openRecord();
        this.fixState("full");
        this.updateDims();
        this.saveState("full");
        canv.eventManager.closeRecord();
    }
    // translate resize

    canResize(x: number, y: number) {
        if (this.presentationUID) {
            const newWidth = this.width + x;
            const newHeight = this.height + y;

            if (newWidth < MIN_SIZE || newHeight < MIN_SIZE) return false;
        }

        return true;
    }

    canTranslateWithPresentation(fromPresentation: boolean): boolean {
        // программный сдвиг всегда разрешён
        if (this.isProgrammaticMove) return true;

        if (this.presentationUID === "") {
            return true;
        } else if (this.presentationGroupUID) {
            const selectedGroup = canv.canvasController.selected.find(
                (group) => group.UID === this.presentationGroupUID,
            );
            return !!selectedGroup;
        } else if (this.presentationUID) {
            const selectedWithPresentation = canv.canvasController.selected.some(
                (g) => g.onlyChild()?.UID === this.presentationUID,
            );

            if (!selectedWithPresentation) return false;

            return fromPresentation;
        } else {
            const widget =
                this.presentationUID && canv.canvasController.FindByUID(this.presentationUID);

            if (widget instanceof PresentationWidget) {
                this.presentationGroupUID = widget.parentUID;
                return false;
            }
        }

        return true;
    }

    translate(x: number, y: number, fromPresentation = false) {
        if (!this.canTranslateWithPresentation(fromPresentation)) return;

        this.startX += x;
        this.startY += y;
        this.updateDims();

        const groups = frameOverlappedManager.getGroups(this.UID);

        for (const group of groups) {
            group.translateGroup(x, y, false, true);
        }

        if (this.rotatingGroups) {
            for (const group of this.rotatingGroups) {
                group.translateGroup(x, y);
            }
        }
    }

    resizingStart() {
        const groups = this.getOverlappedGroups();

        for (const group of groups) {
            group.fixWidgets("resize");
            frameOverlappedManager.addGroup(this.UID, group);
        }

        if (this.presentationUID) {
            const presentation = canv.canvasController.FindByUID(
                this.presentationUID,
            ) as PresentationWidget;

            if (presentation) {
                presentation.parent.fixWidgets("resize");
                for (let i = 0; i < presentation.slides.length; i++) {
                    const group = canv.canvasController.FindByUID(
                        presentation.slides[i].frameUID,
                    ) as Group;
                    if (!group) continue;

                    if (group.UID !== this.parent.UID) {
                        group.fixWidgets("resize");
                        group.groupTranslatingStart();
                    }
                }
            }
        }
    }

    resizingStop() {
        // Сохраняем состояние всех групп, захваченных этим фреймом
        const groups = frameOverlappedManager.getGroups(this.UID);
        for (const group of groups) {
            group.saveWidgets("resize");
        }
        if (this.presentationUID) {
            const presentation = canv.canvasController.FindByUID(
                this.presentationUID,
            ) as PresentationWidget;

            if (presentation) {
                presentation.parent.saveWidgets("resize");
                for (let i = 0; i < presentation.slides.length; i++) {
                    const group = canv.canvasController.FindByUID(
                        presentation.slides[i].frameUID,
                    ) as Group;
                    if (!group) continue;

                    if (group.UID !== this.parent.UID) {
                        group.saveWidgets("resize");
                        group.groupTranslatingStop();
                    }
                }
            }
        }
    }

    resize(x: number, y: number, catchPointer: string, fromPresentation = false) {
        if (!this.canResize(x, y)) return;

        if (this.presentationUID) {
            const selectedWithPresentation = canv.canvasController.selected.some(
                (g) => g.onlyChild()?.UID === this.presentationUID,
            );

            if (catchPointer !== "bottomRight" || (selectedWithPresentation && !fromPresentation))
                return;
        }

        const oldWidth = this.width;
        const oldHeight = this.height;

        this.width += x;
        this.height += y;
        this.updateDims();
        this.aspectRatio = AspectRatioEnum.CUSTOM;

        if (this.presentationUID) {
            const presentation = canv.canvasController.FindByUID(
                this.presentationUID,
            ) as PresentationWidget;

            if (presentation) {
                presentation.resizeFromFrame(this, this.width, this.height, oldWidth, oldHeight);
            }
        }
    }

    // Обработчики widget menu

    menuColor(color: string) {
        this.color = color;
        if (lightOrDark(this.color) === "light") {
            this.textName.menuTextColor("black", true);
        } else {
            this.textName.menuTextColor("white", true);
        }
    }

    menuHide(mode: boolean) {
        this.isHidden = mode;

        const children = canv.canvasController.hashGrid?.queryArea({
            startX: this.startX,
            startY: this.startY,
            width: this.width,
            height: this.height,
        });

        children?.forEach((g: Group) => {
            const element = g.onlyChild();

            if (
                element instanceof EmbedWidget &&
                element.isInArea({
                    x: this.startX,
                    y: this.startY,
                    width: this.width,
                    height: this.height,
                }) &&
                g.indexByParent > this.parent.indexByParent
            ) {
                element.setHidden(mode);
            }
        });
    }

    applyLandscapeRatio(r: number) {
        const MIN_SIZE = 1;

        // защита от 0 и отрицательных значений
        const safeWidth = this.width > 0 ? this.width : MIN_SIZE;
        const safeHeight = this.height > 0 ? this.height : MIN_SIZE;

        // если размеры некорректны — просто возвращаем безопасные
        if (this.height <= 0 || !isFinite(this.height)) {
            return {
                newWidth: safeWidth,
                newHeight: safeHeight,
            };
        }

        const current = safeWidth / safeHeight;
        const EPS = 0.03;

        let newWidth = safeWidth;
        let newHeight = safeHeight;

        if (Math.abs(current - r) < EPS) {
            newHeight = safeWidth / r;

            if (!Number.isFinite(newHeight) || newHeight <= 0) {
                return { newWidth: safeWidth, newHeight: safeHeight };
            }

            return { newWidth, newHeight };
        }

        // сохраняем площадь
        const area = safeWidth * safeHeight;

        newWidth = Math.sqrt(area * r);
        newHeight = newWidth / r;

        if (!Number.isFinite(newWidth) || !Number.isFinite(newHeight)) {
            return { newWidth: safeWidth, newHeight: safeHeight };
        }

        return { newWidth, newHeight };
    }

    setAspectRatio(ratio: AspectRatioEnum) {
        if (ratio === this.aspectRatio) {
            return;
        }

        canv.eventManager.openRecord();

        // если только меняем режим — фиксируем full/state
        if (ratio === "custom") {
            this.fixState("full");

            this.aspectRatio = ratio;

            this.saveState("full");
            canv.eventManager.closeRecord();
            return;
        }

        this.fixState("resize");

        let newWidth = this.width;
        let newHeight = this.height;

        switch (ratio) {
            case AspectRatioEnum.R16_9: {
                ({ newWidth, newHeight } = this.applyLandscapeRatio(16 / 9));
                break;
            }

            case AspectRatioEnum.R4_3: {
                ({ newWidth, newHeight } = this.applyLandscapeRatio(4 / 3));
                break;
            }

            case AspectRatioEnum.R1_1: {
                ({ newWidth, newHeight } = this.applyLandscapeRatio(1));
                break;
            }

            case AspectRatioEnum.A4: {
                ({ newWidth, newHeight } = this.applyLandscapeRatio(210 / 297));
                break;
            }

            default:
                canv.eventManager.closeRecord();
                return;
        }

        const oldWidth = this.width;
        const oldHeight = this.height;

        this.width = newWidth;
        this.height = newHeight;
        this.aspectRatio = ratio;

        if (this.presentationUID) {
            const presentation = canv.canvasController.FindByUID(
                this.presentationUID,
            ) as PresentationWidget;

            if (presentation) {
                presentation.resizeFromFrame(this, newWidth, newHeight, oldWidth, oldHeight);
            }
        }

        this.updateDims();
        this.saveState("resize");
        canv.eventManager.closeRecord();
    }

    getWidgetOptions(isLocked?: boolean) {
        const widgetOptions: widgetOption[] = [];

        if (!isLocked) {
            widgetOptions.push(new widgetOption("color", this.color, this.UID));
        }

        widgetOptions.push(new widgetOption("frameAspectRatio", this.aspectRatio, this.UID));

        if (
            (this.presentationUID === "" || this.presentationUID === null) &&
            canv.canvasController.selected.length === 1
        ) {
            widgetOptions.push(new widgetOption("presentationCreate", null, this.UID));
        }

        if ((canv.isTeamMember() && canv.isBoardModeEditOrOwner()) || canv.isBoardModeOwner()) {
            widgetOptions.push(new widgetOption("hide", this.isHidden, this.UID));
        }
        this.textName.setEditTextMobile(widgetOptions, this.UID);

        return widgetOptions;
    }

    setPresentationUID(UID: string | null) {
        this.fixState("presentationFrame");
        this.presentationUID = UID;
        this.fastAction = "";
        this.saveState("presentationFrame");
    }

    beforeDeletion() {
        if (this.presentationUID) {
            const presentation = canv.canvasController.FindByUID(
                this.presentationUID,
            ) as PresentationWidget;

            if (presentation) {
                presentation.deleteSlide(this);
            }
        }
    }

    getHandlerForOption(name: string) {
        if (name === "color") {
            return this.menuColor;
        }
        if (name === "hide") {
            return this.menuHide;
        }
        if (name === "frameAspectRatio") {
            return this.setAspectRatio;
        }
    }

    // Далее для statesManager

    getFixType(reason: string): FrameStateFixType {
        switch (reason) {
            case "move":
                return "move";
            case "resize":
                return "resize";
            case "wm_color":
                return "color";
            case "wm_hide":
                return "hide";
            case "presentationFrame":
                return "presentationFrame";
            default:
                return "full";
        }
    }

    getState<T extends FrameStateFixType>(fixType: T): FrameStateMap[T] {
        switch (fixType) {
            case "move":
                return {
                    startX: this.startX,
                    startY: this.startY,
                } as FrameStateMap[T];
            case "resize":
                return {
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                    aspectRatio: this.aspectRatio,
                } as FrameStateMap[T];
            case "color":
                return {
                    color: this.color,
                } as FrameStateMap[T];
            case "hide":
                return {
                    isHidden: this.isHidden,
                } as FrameStateMap[T];
            case "presentationFrame":
                return {
                    presentationUID: this.presentationUID,
                } as FrameStateMap[T];
            default:
                return {
                    UID: this.UID,
                    parentUID: this.parentUID,
                    entityType: "frameWidget",
                    startX: this.startX,
                    startY: this.startY,
                    width: this.width,
                    height: this.height,
                    color: this.color,
                    isHidden: this.isHidden,
                    textName: this.textName.getState("full"),
                    aspectRatio: this.aspectRatio,
                    presentationUID: this.presentationUID,
                } as FrameStateMap[T];
        }
    }

    loadState<T extends FrameStateFixType>(stateObj: FrameStateMap[T], fixType: T): void {
        if (fixType === "full") {
            const state = stateObj as FrameStateFull;
            this.UID = getStringProperty("UID", state);
            this.parentUID = getStringProperty("parentUID", state);
            this.startX = getNumberProperty("startX", state);
            this.startY = getNumberProperty("startY", state);
            this.width = getNumberProperty("width", state);
            this.height = getNumberProperty("height", state);
            this.color = getColorProperty("color", state);
            this.isHidden = getBoolProperty("isHidden", state, false);
            this.presentationUID = getStringProperty("presentationUID", state) || null;
            //todo убрать || "custom" когда на бэке добавят это поле
            this.aspectRatio =
                (getStringProperty("aspectRatio", state) as AspectRatioEnum) ||
                AspectRatioEnum.CUSTOM;

            this.textName.setKeepMinWidth(false);
            this.textName.onlyLineMode = true;
            this.textName.setEditingAutosaveHandler(() => {
                canv.eventManager.openRecord();
                this.saveState("nameFocus");
                canv.eventManager.closeRecord();
                this.fixState("nameFocus");
            });

            //Здесь и ниже патч для имен фрейма в две строки, TODO: удалить через пару недель
            const modifiedState: FrameStateFull["textName"] = {
                ...state.textName,
                resizeMode: "auto",
                hardWidth: 0,
            };

            this.textName.loadState(modifiedState, "full");

            const titleText = this.textName.getText();

            if (titleText.includes("\n")) {
                this.textName.setText(titleText);
            }

            if (getStringProperty("presentationUID", state)) {
                this.fastAction = "";
            }
            if (isBrowser()) {
                if (lightOrDark(this.color) === "light") {
                    this.textName.menuTextColor("black", true);
                } else {
                    this.textName.menuTextColor("white", true);
                }
            }
        } else if (fixType === "move") {
            const state = stateObj as FrameStateMove;
            this.startX = getNumberProperty("startX", state);
            this.startY = getNumberProperty("startY", state);
        } else if (fixType === "resize") {
            const state = stateObj as FrameStateResize;
            this.startX = getNumberProperty("startX", state);
            this.startY = getNumberProperty("startY", state);
            this.width = getNumberProperty("width", state);
            this.height = getNumberProperty("height", state);
        } else if (fixType === "color") {
            const state = stateObj as FrameStateColor;
            this.color = getColorProperty("color", state);
        } else if (fixType === "hide") {
            const state = stateObj as FrameStateHide;
            this.isHidden = getBoolProperty("isHidden", state, false);
        } else if (fixType === "presentationFrame") {
            this.presentationUID = getStringProperty("presentationUID", stateObj);
        }

        // Именно два раза
        this.updateDims();
    }

    constructorName: "frame" = "frame";
    startX = 0;
    startY = 0;
    width = 0;
    height = 0;

    textName: TextWidgetV2;

    color = "#0c8de9";
    isHidden = false;

    rotatingGroups: Group[] | null = null;
    isNameEditing = false;
    rotablesWidgetsUids: string[] = [];
    allowOptionsForLocked = true;
    presentationUID: string | null = null;
    presentationGroupUID: string | null = null;
    isProgrammaticMove: boolean = false;

    colorMap = {
        "#111111": "#F4F4F4",
        "#E03E1A": "#F9E4DF",
        "#f79722": "#FDF2E1",
        "#f3c11b": "#FEF7E1",
        "#198f51": "#E0F2E7",
        "#0c8de9": "#DFEFFD",
        "#8a38f5": "#EDE4FD",
        "#ffffff": "#ffffff",

        "#383838": "#F4F4F4",
        "#d9d9d9": "#F4F4F4",
        "#fbbcb6": "#F9E4DF",
        "#ffc470": "#FDF2E1",
        "#f9df90": "#FEF7E1",
        "#a1e8b9": "#E0F2E7",
        "#a8d7fa": "#DFEFFD",
        "#d6b6fb": "#EDE4FD",
    };
    aspectRatio: AspectRatioEnum = AspectRatioEnum.CUSTOM;
}
